import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import {
  Box,
  Typography,
  Paper,
  Chip,
  LinearProgress,
  Skeleton,
  Button,
  Alert
} from '@mui/material'
import ScubaDivingIcon from '@mui/icons-material/ScubaDiving'
import GroupIcon from '@mui/icons-material/Group'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import PlaceIcon from '@mui/icons-material/Place'
import VerifiedIcon from '@mui/icons-material/Verified'
import DepthIcon from '@mui/icons-material/Straighten'
import EventIcon from '@mui/icons-material/Event'
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'
import PersonIcon from '@mui/icons-material/Person'
import Wrapper from '../components/Wrapper'
import routes from './routes'
import { useSessionStore } from '../state'
import { getUserDiveStats, DiveStats } from '../services/homeStatsService'

const Home = () => {
  const user = useSessionStore(s => s.user)
  const profile = useSessionStore(s => s.profile)
  const accreditationStatus = profile?.accreditation?.status ?? null

  const [stats, setStats] = useState<DiveStats | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!user?.uid) return
    let cancelled = false
    setLoading(true)
    getUserDiveStats(user.uid)
      .then(s => {
        if (!cancelled) setStats(s)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [user?.uid])

  const formatDate = (date: Date) =>
    date.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })

  return (
    <Wrapper>
      {/* Get Accredited Banner */}
      {accreditationStatus === null && (
        <Alert
          severity='info'
          icon={<VerifiedIcon />}
          sx={{ mb: 3, mt: 1 }}
          action={
            <Button
              component={Link}
              to={routes.accreditation}
              color='inherit'
              size='small'
              variant='outlined'
            >
              Get Accredited
            </Button>
          }
        >
          Verify your dive accreditation to unlock full access.
        </Alert>
      )}

      {accreditationStatus === 'pending' && (
        <Alert severity='warning' icon={<VerifiedIcon />} sx={{ mb: 3, mt: 1 }}>
          Your accreditation is being reviewed — we&apos;ll notify you once
          it&apos;s verified.
        </Alert>
      )}

      {/* Credit Balance */}
      {profile && profile.creditBalance > 0 && (
        <Paper
          variant='outlined'
          sx={{
            p: 2,
            mb: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            background: 'linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%)',
            borderColor: '#81c784'
          }}
        >
          <AccountBalanceWalletIcon sx={{ fontSize: 32, color: '#388e3c' }} />
          <Box>
            <Typography variant='subtitle2' color='text.secondary'>
              Account Credit
            </Typography>
            <Typography variant='h5' fontWeight='bold' color='success.dark'>
              ${profile.creditBalance.toFixed(2)}
            </Typography>
          </Box>
        </Paper>
      )}

      {/* Next Dive */}
      {!loading && stats?.nextDive && (
        <Paper
          variant='outlined'
          sx={{
            p: 2,
            mb: 3,
            background: 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)',
            borderColor: '#90caf9'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <EventIcon color='primary' />
            <Typography variant='subtitle2' fontWeight='bold'>
              Next Dive
            </Typography>
          </Box>
          <Typography variant='h6' sx={{ mb: 0.5 }}>
            {stats.nextDive.siteName}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            {formatDate(stats.nextDive.dateTime.toDate())} &middot;{' '}
            {stats.nextDive.duration} min &middot; {stats.nextDive.depthTier}
          </Typography>
          <Box sx={{ mt: 1 }}>
            <Chip
              size='small'
              label={stats.nextDive.type === 'guided' ? 'Guided' : 'Buddy'}
              color={stats.nextDive.type === 'guided' ? 'secondary' : 'primary'}
              variant='outlined'
            />
          </Box>
        </Paper>
      )}

      {/* Stats Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
          {[1, 2, 3].map(i => (
            <Skeleton
              key={i}
              variant='rounded'
              sx={{ flex: '1 1 140px', height: 100, minWidth: 140 }}
            />
          ))}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
          <StatCard
            icon={<ScubaDivingIcon sx={{ fontSize: 32, color: '#0288d1' }} />}
            value={stats?.totalDives ?? 0}
            label='Total Dives'
          />
          <StatCard
            icon={<GroupIcon sx={{ fontSize: 32, color: '#7b1fa2' }} />}
            value={stats?.uniqueBuddies ?? 0}
            label='Dive Buddies'
          />
          <StatCard
            icon={<CalendarTodayIcon sx={{ fontSize: 32, color: '#388e3c' }} />}
            value={stats?.scheduledCount ?? 0}
            label='Upcoming'
            to={`${routes.dives}?tab=scheduled`}
          />
        </Box>
      )}

      {/* My Profile Link */}
      {user && (
        <Button
          component={Link}
          to={`/profile/${user.uid}`}
          variant='outlined'
          startIcon={<PersonIcon />}
          fullWidth
          sx={{ mb: 3 }}
        >
          My Public Profile
        </Button>
      )}

      {/* Depth Breakdown */}
      {!loading && stats && stats.totalDives > 0 && (
        <Paper variant='outlined' sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <DepthIcon color='action' />
            <Typography variant='subtitle2' fontWeight='bold'>
              Depth Breakdown
            </Typography>
          </Box>
          {stats.depthBreakdown.map(({ tier, count }) => (
            <Box key={tier} sx={{ mb: 1.5 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: 0.5
                }}
              >
                <Typography variant='body2'>{depthLabel(tier)}</Typography>
                <Typography variant='body2' color='text.secondary'>
                  {count} dive{count !== 1 ? 's' : ''}
                </Typography>
              </Box>
              <LinearProgress
                variant='determinate'
                value={stats.totalDives ? (count / stats.totalDives) * 100 : 0}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: 'grey.200',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    bgcolor: depthColor(tier)
                  }
                }}
              />
            </Box>
          ))}
        </Paper>
      )}

      {/* Popular Dive Sites */}
      {!loading && stats && stats.popularSites.length > 0 && (
        <Paper variant='outlined' sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <PlaceIcon color='action' />
            <Typography variant='subtitle2' fontWeight='bold'>
              Top Dive Sites
            </Typography>
          </Box>
          {stats.popularSites.map(({ name, count }, i) => (
            <Box
              key={name}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                py: 1,
                borderBottom:
                  i < stats.popularSites.length - 1 ? '1px solid' : 'none',
                borderColor: 'divider'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant='body2'
                  sx={{
                    width: 20,
                    textAlign: 'center',
                    fontWeight: 'bold',
                    color: 'text.secondary'
                  }}
                >
                  {i + 1}
                </Typography>
                <Typography variant='body2'>{name}</Typography>
              </Box>
              <Chip
                size='small'
                label={`${count}x`}
                variant='outlined'
                color='primary'
              />
            </Box>
          ))}
        </Paper>
      )}

      {/* Empty state */}
      {!loading && stats && stats.totalDives === 0 && (
        <Paper variant='outlined' sx={{ p: 3, textAlign: 'center', mb: 3 }}>
          <ScubaDivingIcon
            sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }}
          />
          <Typography variant='h6' color='text.secondary' gutterBottom>
            No dives yet
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Find your first dive or create one to start building your stats.
          </Typography>
          <Button
            component={Link}
            to={routes.dives}
            variant='contained'
            size='small'
          >
            Find Dives
          </Button>
        </Paper>
      )}
    </Wrapper>
  )
}

// --- Helpers ---

function StatCard({
  icon,
  value,
  label,
  to
}: {
  icon: React.ReactNode
  value: number
  label: string
  to?: string
}) {
  const card = (
    <Paper
      variant='outlined'
      sx={{
        ...(to ? { width: '100%' } : { flex: '1 1 140px', minWidth: 140 }),
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.5,
        cursor: to ? 'pointer' : undefined
      }}
    >
      {icon}
      <Typography variant='h5' fontWeight='bold'>
        {value}
      </Typography>
      <Typography variant='caption' color='text.secondary'>
        {label}
      </Typography>
    </Paper>
  )

  if (to) {
    return (
      <Link
        to={to}
        style={{
          textDecoration: 'none',
          color: 'inherit',
          flex: '1 1 140px',
          minWidth: 140
        }}
      >
        {card}
      </Link>
    )
  }

  return card
}

function depthLabel(tier: string): string {
  switch (tier) {
    case '18m':
      return '0 – 18 m (Shallow)'
    case '30m':
      return '18 – 30 m (Mid)'
    case '30m+':
      return '30 m+ (Deep)'
    default:
      return tier
  }
}

function depthColor(tier: string): string {
  switch (tier) {
    case '18m':
      return '#4fc3f7'
    case '30m':
      return '#0288d1'
    case '30m+':
      return '#01579b'
    default:
      return '#90a4ae'
  }
}

export default Home
